import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ColorPicker, Field, FormError, TimezoneSelect, VisibilityPicker } from '@/components/app/common';
import { createStore } from '@/lib/store';
import { post } from '@/lib/api';
import { COLORS, TZ_DEFAULT } from '@/lib/format';
import { keys } from '@/lib/queries';
import { notify } from '@/lib/notify';
import type { Feed, Visibility } from '@/lib/types';

const open = createStore<null | 'manual' | 'external'>(null);
export const openCreateFeed = (kind: 'manual' | 'external') => open.set(kind);

export function CreateFeedDialog() {
  const kind = open.use();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const external = kind === 'external';
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [tz, setTz] = useState(TZ_DEFAULT);
  const [color, setColor] = useState(COLORS[0]);
  const [vis, setVis] = useState<Visibility>('unlisted');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!kind) return;
    const n = (qc.getQueryData<Feed[]>(keys.feeds) ?? []).length;
    setName('');
    setUrl('');
    setTz(TZ_DEFAULT);
    setColor(COLORS[n % COLORS.length]);
    setVis('unlisted');
    setErr('');
  }, [kind, qc]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (external && !url.trim()) return setErr('Please paste the calendar’s URL.');
    if (!name.trim()) return setErr('Please give your calendar a name.');
    setBusy(true);
    try {
      const r = await post<{ id: string; sync?: { ok: boolean; message: string } }>('/feeds', { name, timezone: tz, color, visibility: vis, kind, ...(external ? { source_url: url } : {}) });
      await qc.invalidateQueries({ queryKey: keys.feeds });
      if (r.sync) notify(r.sync.message, { kind: r.sync.ok ? 'success' : 'error' });
      else notify('Calendar created');
      open.set(null);
      navigate(`/feeds/${r.id}`);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && open.set(null)}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={submit} className="grid grid-cols-[minmax(0,1fr)] gap-5" noValidate onInput={() => setErr('')}>
          <DialogHeader>
            <DialogTitle>{external ? 'Connect a calendar' : 'New calendar'}</DialogTitle>
            <DialogDescription>{external ? 'Subscribe to a calendar published somewhere else. It stays in sync and can be used as a layer.' : 'A calendar people can subscribe to. Add your own events and include other calendars as layers.'}</DialogDescription>
          </DialogHeader>
          {external ? (
            <Field label="Calendar URL" htmlFor="cf-url" hint="An .ics or webcal link — e.g. Google’s public holiday calendar or a school website feed. Synced every hour.">
              <Input id="cf-url" name="source_url" autoFocus inputMode="url" placeholder="https://… or webcal://…" value={url} onChange={(e) => setUrl(e.target.value)} />
            </Field>
          ) : null}
          <Field label="Name" htmlFor="cf-name">
            <Input id="cf-name" name="name" maxLength={80} autoFocus={!external} placeholder={external ? 'e.g. BC statutory holidays' : 'e.g. Grade 3 · Room 12'} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2">
            <Field label="Time zone" htmlFor="cf-tz">
              <TimezoneSelect id="cf-tz" value={tz} onChange={setTz} />
            </Field>
            <Field label="Color">
              <ColorPicker value={color} onChange={setColor} />
            </Field>
          </div>
          <Field label="Who can subscribe?">
            <VisibilityPicker value={vis} onChange={setVis} />
          </Field>
          <FormError>{err}</FormError>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => open.set(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? (external ? 'Syncing…' : 'Creating…') : external ? 'Connect & sync' : 'Create calendar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
